// Route constants for the seller-service owner self-onboarding surface.
// seller-service mounts everything under the `/api` global prefix.
//
// Two families, and the split is the whole security model (plan §3.1):
//
//   PUBLIC_*  — anonymous. The long-lived invite token from the email sits in
//               the path, and is exchanged for a session exactly once.
//   SESSION_* — every other call. Authorised by the short-lived session token in
//               an `Authorization: Bearer` header, never by the invite token.
//
// The invite token must never appear on a SESSION_* path. Nothing server-side
// can enforce that (plan risk 9) — it is held here, by there being no builder
// that would let it.
//
// A third family, SELLER_*, rides the seller's own JWT: a logged-in seller asks
// for the same kind of session for their own owner record, keyed by listing
// request — no invite token involved at all.

const PUBLIC_OWNER_ONBOARDING = '/api/public/owners/onboarding'
const OWNER_ONBOARDING = '/api/owners/onboarding'
const sellerOwnerOnboarding = (listingRequestId: string) =>
  `/api/sellers/me/listing-requests/${encodeURIComponent(listingRequestId)}/owner-onboarding`

export const OWNER_ONBOARDING_ENDPOINT_PATHS = {
  RESOLVE_INVITE: (token: string) => `${PUBLIC_OWNER_ONBOARDING}/${encodeURIComponent(token)}`,
  CONTEXT: `${OWNER_ONBOARDING}/context`,
  // Deliberately not the shared `/api/countries`. That one sits behind the JWT
  // guard, and an owner has no seller JWT — the 401 it returns trips the
  // auth interceptor on `sellerApi`, which clears storage and redirects to a
  // login page the owner can never pass.
  COUNTRIES: `${OWNER_ONBOARDING}/countries`,
  KYC_SESSION: `${OWNER_ONBOARDING}/kyc/session`,
  // Where the identity check stands, as the server sees it. The only thing that
  // survives a page refresh — everything the SumSub iframe showed lived in the tab.
  KYC_STATUS: `${OWNER_ONBOARDING}/kyc/status`,
  // Separate from KYC_SESSION because it resets the SumSub applicant, which must
  // never happen as a side effect of the SDK refreshing an expired token.
  KYC_RETRY: `${OWNER_ONBOARDING}/kyc/session/retry`,
  UAE_PASS_INITIATE: `${OWNER_ONBOARDING}/uae-pass/initiate`,
  UAE_PASS_COMPLETE: `${OWNER_ONBOARDING}/uae-pass/complete`,
  // Non-production fallback for the UAE Pass step. 404 wherever it is switched off.
  EMAIL_OTP_SEND: `${OWNER_ONBOARDING}/email-otp/send`,
  EMAIL_OTP_VERIFY: `${OWNER_ONBOARDING}/email-otp/verify`,
  PERSONAL_DETAILS: `${OWNER_ONBOARDING}/personal-details`,
  COMPANY_PROFILE: `${OWNER_ONBOARDING}/company-profile`,
  REPRESENTATIVES: `${OWNER_ONBOARDING}/representatives`,
  // One path, two verbs: POST submits, GET reads it back masked.
  BANK_ACCOUNT: `${OWNER_ONBOARDING}/bank-account`,
  OWNERSHIP_CONFIRMATION: `${OWNER_ONBOARDING}/ownership-confirmation`,
  COMPLETE: `${OWNER_ONBOARDING}/complete`,
  SELLER_AVAILABILITY: (listingRequestId: string) => sellerOwnerOnboarding(listingRequestId),
  SELLER_SESSION: (listingRequestId: string) =>
    `${sellerOwnerOnboarding(listingRequestId)}/session`,
} as const
